import { useState } from 'react';

import { Icon } from '../components/Icon.js';
// Styles live with the panel's stylesheet, as in the mock — the stepper is
// the panel's title, not a standalone widget.
import styles from './InboxPanel.module.css';

/** The three views the stepper cycles through, in order. */
export type InboxView = 'all' | 'jobs' | 'direct';

const VIEWS: { value: InboxView; label: string }[] = [
  { value: 'all', label: 'All Messages' },
  { value: 'jobs', label: 'Job Messages' },
  { value: 'direct', label: 'Direct Messages' },
];

/**
 * Read `?view=` off the URL, ignoring anything that isn't one of ours — the same
 * contract as `sectionFromTab` and `toHandoffIntent`: hosts forward query
 * parameters blind, so a stale or mistyped link must land on the default rather
 * than break. Read off VIEWS so a view added there needs nothing here.
 */
export function toInboxView(raw: string | null): InboxView | null {
  return VIEWS.find((v) => v.value === raw)?.value ?? null;
}

interface KindStepperProps {
  view: InboxView;
  onChange: (view: InboxView) => void;
}

/**
 * The inbox title as a looping view switcher. Both chevrons and the peek change
 * the view; the peek is the *next* one, so clicking what you can see moves you
 * there.
 *
 * The slide direction is passed explicitly rather than derived from the index,
 * because the stepper wraps: going from Direct to All is a forward step even
 * though the index decreases.
 */
export function KindStepper({ view, onChange }: KindStepperProps) {
  const [dir, setDir] = useState<1 | -1 | null>(null);

  const index = VIEWS.findIndex((v) => v.value === view);
  const current = VIEWS[index] ?? VIEWS[0]!;
  const prev = VIEWS[(index - 1 + VIEWS.length) % VIEWS.length]!;
  const next = VIEWS[(index + 1) % VIEWS.length]!;

  const go = (to: InboxView, direction: 1 | -1) => {
    setDir(direction);
    onChange(to);
  };

  return (
    <div className={styles.kindNav}>
      <button
        type="button"
        className={styles.kindArrow}
        onClick={() => go(prev.value, -1)}
        aria-label={`Previous view: ${prev.label}`}
      >
        <Icon name="chevronRight" size={18} />
      </button>

      <div className={styles.kindViewport}>
        {/* Keyed on the view so a step remounts the track and replays the slide.
            A mount that was not a step has no direction yet, so it matches no
            animation rule: no slide on first paint or on leaving selection. */}
        <div key={view} data-dir={dir} className={styles.kindTrack}>
          <h1 className={styles.title}>{current.label}</h1>
          <button
            type="button"
            className={styles.peek}
            onClick={() => go(next.value, 1)}
            aria-label={`Go to ${next.label}`}
          >
            {next.label}
          </button>
        </div>
      </div>

      <button
        type="button"
        className={styles.kindArrow}
        onClick={() => go(next.value, 1)}
        aria-label={`Next view: ${next.label}`}
      >
        <Icon name="chevronRight" size={18} />
      </button>
    </div>
  );
}
